import type { ChartLayout } from "../types.js";
import { ensureAn26DesignStyles } from "./injectStyles.js";
import { SERIES_COLORS, SERIES_FADED_COLORS, type BarChartPayload } from "../normalize.js";
import { linearTicks } from "./geometry/axisTicks.js";
import { renderD3ColumnChart } from "./d3/column.js";
import { formatBarDisclaimerHtml } from "./richText.js";

/** Value axis never compresses below this, even when every value is small. */
const BAR_VALUE_FLOOR_MAX = 30;
const BAR_VALUE_TICK_INTERVAL = 5;

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function fmtPct(n: number): string {
  return `${n.toFixed(2)}`;
}

export interface BarChartRenderOptions {
  title?: string | null;
  subtitle?: string | null;
  disclaimer?: string | null;
  layout?: ChartLayout;
}

function buildTableHtml(data: BarChartPayload, inceptionDate: string | undefined): string {
  const corner = `Month End (As of ${esc(data.asOf)})`;
  const trailing = data.trailingColumn;
  const periodHeads = data.periods
    .map((p, i) => {
      const isLast = i === data.periods.length - 1;
      const sub =
        !trailing && isLast && inceptionDate
          ? `<br/><span class="an26-table__th-sub">${esc(inceptionDate)}</span>`
          : "";
      return `<th>${esc(p)}${sub}</th>`;
    })
    .join("");
  const trailingHead = trailing ? `<th>${esc(trailing.label)}</th>` : "";
  const rows = data.series
    .map((s, si) => {
      const color = SERIES_COLORS[si % SERIES_COLORS.length];
      const cells = data.periods
        .map((_, pi) => {
          const v = s.data[pi];
          const t = v != null && Number.isFinite(v) ? fmtPct(v) : "—";
          return `<td>${esc(t)}</td>`;
        })
        .join("");
      const trailCell = trailing ? `<td>${esc(trailing.values[si] ?? "—")}</td>` : "";
      return `<tr>
        <td class="an26-table__label"><span class="an26-table__swatch" style="background:${esc(color)}"></span>${esc(s.name)}</td>
        ${cells}${trailCell}
      </tr>`;
    })
    .join("");
  return `<div class="an26-table-wrap"><table class="an26-table an26-table--bar" role="grid">
    <thead><tr><th class="an26-table__corner">${corner}</th>${periodHeads}${trailingHead}</tr></thead>
    <tbody>${rows}</tbody>
  </table></div>`;
}

export function renderBarChart(
  container: HTMLElement,
  data: BarChartPayload,
  opts?: BarChartRenderOptions
): () => void {
  ensureAn26DesignStyles();
  const embedded = opts?.layout === "embedded";
  const title = opts?.title ?? "Average Annual Total Returns";
  const subtitle = opts?.subtitle ?? (data.asOf ? `As of ${data.asOf}` : "");
  const disclaimerRaw =
    opts?.disclaimer === null ? "" : (opts?.disclaimer ?? data.disclaimer ?? "");
  const disclaimer =
    !embedded && disclaimerRaw
      ? formatBarDisclaimerHtml(disclaimerRaw, data.benchmarkName)
      : "";
  const inception = data.inceptionDate;

  const chartHostId = `an26-bar-hc-${Math.random().toString(36).slice(2, 9)}`;
  const head = embedded
    ? ""
    : `<header class="an26-root__head">
        <h3 class="an26-root__title">${esc(String(title))}</h3>
        ${subtitle ? `<p class="an26-root__subtitle">${esc(subtitle)}</p>` : ""}
      </header>`;
  const rootClass = embedded ? "an26-root an26-root--bar an26-root--embedded" : "an26-root an26-root--bar";
  container.innerHTML = `
    <div class="${rootClass}">
      ${head}
      <div class="an26-root__chart-wrap">
        <div id="${chartHostId}" class="an26-root__chart"></div>
      </div>
      ${buildTableHtml(data, inception)}
      ${disclaimer ? `<p class="an26-bar__disc">${disclaimer}</p>` : ""}
    </div>`;

  const mount = container.querySelector(`#${chartHostId}`) as HTMLElement | null;
  if (!mount || !data.periods.length || !data.series.length) {
    return () => container.replaceChildren();
  }

  const finiteValues = data.series.flatMap((s) => s.data.filter((n) => Number.isFinite(n)));
  const valueTicks = linearTicks({
    softMin: 0,
    dataMin: finiteValues.length ? Math.min(...finiteValues) : 0,
    dataMax: finiteValues.length ? Math.max(...finiteValues) : 0,
    floorMax: BAR_VALUE_FLOOR_MAX,
    interval: BAR_VALUE_TICK_INTERVAL,
  });

  const cleanup = renderD3ColumnChart(mount, data, { valueTicks, embedded, esc });
  return () => {
    cleanup();
    container.replaceChildren();
  };
}
